import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import { useARPEntriesDataContext } from '../../context/arpEntriesDataContext';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import FilterListOffOutlinedIcon from '@mui/icons-material/FilterListOffOutlined';
import filterStyles from './filterStyles';
import { formatMacAddress } from '../../utils/macFormatter';
import { getStringListParam } from '../../utils/queryUtils';
import { CoreDevicesFilterDataType } from '../../types/filterTypes';

export const DEFAULT_CORE_DEVICES_FILTER_STATE: CoreDevicesFilterDataType = {
  mac_address: [],
  ip_address: '',
  interface: '',
  vlan: '',
  dhcp_static: '',
  dhcp_reservation: '',
  dhcp_state: '',
};

/**
 * Parses the raw (comma-separated) mac_address TextField value into a
 * normalized list. Unlike `getStringListParam` (used for URL params), this
 * never returns `null` - an empty/whitespace-only input simply yields `[]`,
 * which `matchesFilter`/`hasFilterData` treat as "no MAC filter active".
 */
const parseMacListInput = (raw: string): string[] =>
  raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

interface CoreDevicesFilterMenuProps {
  filterState: CoreDevicesFilterDataType;
  setFilterState: React.Dispatch<React.SetStateAction<CoreDevicesFilterDataType>>;
  navigationFilter?: boolean;
  navigationFilterName?: string;
  navigationFilterValue?: string;
}

export default function CoreDevicesFilterMenu(props: CoreDevicesFilterMenuProps) {
  const {
    filterState,
    setFilterState,
    navigationFilter,
    navigationFilterName,
    navigationFilterValue,
  } = props;
  const { allARPEntries, setARPEntries } = useARPEntriesDataContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [badge, setBadge] = React.useState<Boolean>(false);
  const open = Boolean(anchorEl);
  // Raw mac_address input text, decoupled from the parsed array so
  // trailing commas/spaces aren't stripped mid-keystroke.
  const [macInputText, setMacInputText] = React.useState<string>('');

  const resetFilters = () => {
    setFilterState({ ...DEFAULT_CORE_DEVICES_FILTER_STATE, mac_address: [] });
    setMacInputText('');
  };

  const hasFilterData = (filter_data: CoreDevicesFilterDataType): boolean =>
    Object.entries(filter_data).some(([key, item]) =>
      key === 'mac_address' ? (item as string[]).length > 0 : (item as string).trim() !== ''
    );

  const handleClickFilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (open) {
      setAnchorEl(null);
    }
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
  };

  const filter = (filterData: CoreDevicesFilterDataType) => {
    return allARPEntries.filter((obj) => {
      const matchesFilter = (key: keyof CoreDevicesFilterDataType) => {
        if (key === 'mac_address') {
          const macFilters = filterData.mac_address;
          if (macFilters.length === 0) return true;
          if (obj.mac_address == null) return false;

          const rowMac = formatMacAddress(obj.mac_address).toLowerCase();
          return macFilters.some((mac) => rowMac.includes(formatMacAddress(mac).toLowerCase()));
        }

        const filterValue = (filterData[key] as string).trim();
        // @ts-ignore - dynamic column lookup on DeviceARPDataType
        const rowValue = obj[key];
        if (filterValue === '' || rowValue == null) {
          return filterValue === '';
        }

        return rowValue.toLowerCase().includes(filterValue.toLowerCase());
      };

      return (
        matchesFilter('mac_address') &&
        matchesFilter('ip_address') &&
        matchesFilter('interface') &&
        matchesFilter('vlan') &&
        matchesFilter('dhcp_static') &&
        matchesFilter('dhcp_reservation') &&
        matchesFilter('dhcp_state')
      );
    });
  };

  const handleFilterInput = (filterName: string) => (e: any) => {
    e.preventDefault();
    const searchValue: string = e.target.value.toLowerCase();

    if (filterName === 'mac_address') {
      setMacInputText(searchValue);
    }

    const updatedFilterState = {
      ...filterState,
      [filterName]: filterName === 'mac_address' ? parseMacListInput(searchValue) : searchValue,
    } as CoreDevicesFilterDataType;

    setFilterState(updatedFilterState);
    setBadge(hasFilterData(updatedFilterState));

    const filteredArray = filter(updatedFilterState);
    setARPEntries(filteredArray);
  };

  const handleNavigationFilter = (
    filterName: string | undefined,
    filterValue: string | undefined
  ) => {
    if (!filterName) return;

    const updatedFilterState = {
      ...filterState,
      [filterName]: filterName === 'mac_address' ? getStringListParam(filterValue) ?? [] : filterValue ?? '',
    } as CoreDevicesFilterDataType;

    if (filterName === 'mac_address') {
      setMacInputText(updatedFilterState.mac_address.join(', '));
    }

    setFilterState(updatedFilterState);
    setBadge(true);

    const filteredArray = filter(updatedFilterState);
    setARPEntries(filteredArray);
  };

  const handleClearFilter = (filterName: string) => (e: any) => {
    if (filterName === 'mac_address') {
      setMacInputText('');
    }

    const updatedFilterState = {
      ...filterState,
      [filterName]: filterName === 'mac_address' ? [] : '',
    } as CoreDevicesFilterDataType;

    setFilterState(updatedFilterState);
    setBadge(hasFilterData(updatedFilterState));

    const filteredArray = filter(updatedFilterState);
    setARPEntries(filteredArray);
  };

  const handleClearAllFilters = (e: any) => {
    setARPEntries(allARPEntries);
    resetFilters();
    setBadge(false);
  };

  React.useEffect(() => {
    resetFilters();

    if (navigationFilter && navigationFilterValue) {
      handleNavigationFilter(navigationFilterName, navigationFilterValue);
    }
  }, [navigationFilter]);

  return (
    <React.Fragment>
      <Tooltip title="Apply filters">
        <IconButton
          onClick={handleClickFilter}
          size="small"
          sx={{ ml: 2 }}
          aria-controls={open ? 'filter-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          {badge ? (
            <Badge variant="dot" color="primary">
              <FilterListIcon color="primary" fontSize={'large'} />
            </Badge>
          ) : (
            <FilterListIcon color={open ? 'primary' : 'action'} fontSize={'large'} />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Clear filters">
        <IconButton size="small" onClick={handleClearAllFilters}>
          <FilterListOffOutlinedIcon color={badge ? 'primary' : 'action'} fontSize={'large'} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="filter-menu"
        open={open}
        onClose={handleCloseFilter}
        slotProps={{
          paper: {
            sx: { ...filterStyles.menu },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem sx={{ display: 'flex' }}>
          <Button
            sx={{ ...filterStyles.actionButton }}
            variant="outlined"
            onClick={handleClearAllFilters}
          >
            <FilterListOffOutlinedIcon fontSize={'small'} /> Clear
          </Button>
          <Button
            sx={{ ...filterStyles.actionButton }}
            variant="outlined"
            onClick={handleCloseFilter}
          >
            Close
          </Button>
        </MenuItem>
        <Divider />
        <MenuItem onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            label={macInputText ? 'Mac address:' + macInputText : 'Mac address'}
            value={macInputText}
            size="small"
            sx={{ ...filterStyles.textField }}
            onChange={handleFilterInput('mac_address')}
          />
          <IconButton onClick={handleClearFilter('mac_address')}>
            <CloseIcon />
          </IconButton>
        </MenuItem>
        <MenuItem onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            label={
              filterState['ip_address'] ? 'IP address:' + filterState['ip_address'] : 'IP address'
            }
            value={filterState['ip_address'] ? filterState['ip_address'] : ''}
            size="small"
            sx={{ ...filterStyles.textField }}
            onChange={handleFilterInput('ip_address')}
          />
          <IconButton onClick={handleClearFilter('ip_address')}>
            <CloseIcon />
          </IconButton>
        </MenuItem>
        <MenuItem onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            label={filterState['interface'] ? 'Interface:' + filterState['interface'] : 'Interface'}
            value={filterState['interface'] ? filterState['interface'] : ''}
            size="small"
            sx={{ ...filterStyles.textField }}
            onChange={handleFilterInput('interface')}
          />
          <IconButton onClick={handleClearFilter('interface')}>
            <CloseIcon />
          </IconButton>
        </MenuItem>
        <MenuItem onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            label={filterState['vlan'] ? 'VLAN:' + filterState['vlan'] : 'VLAN'}
            value={filterState['vlan'] ? filterState['vlan'] : ''}
            size="small"
            sx={{ ...filterStyles.textField }}
            onChange={handleFilterInput('vlan')}
          />
          <IconButton onClick={handleClearFilter('vlan')}>
            <CloseIcon />
          </IconButton>
        </MenuItem>
        <MenuItem onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            label={
              filterState['dhcp_static']
                ? 'DHCP/static:' + filterState['dhcp_static']
                : 'DHCP/static'
            }
            value={filterState['dhcp_static'] ? filterState['dhcp_static'] : ''}
            size="small"
            sx={{ ...filterStyles.textField }}
            onChange={handleFilterInput('dhcp_static')}
          />
          <IconButton onClick={handleClearFilter('dhcp_static')}>
            <CloseIcon />
          </IconButton>
        </MenuItem>
        <MenuItem onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            label={
              filterState['dhcp_reservation']
                ? 'DHCP reservation:' + filterState['dhcp_reservation']
                : 'DHCP reservation'
            }
            value={filterState['dhcp_reservation'] ? filterState['dhcp_reservation'] : ''}
            size="small"
            sx={{ ...filterStyles.textField }}
            onChange={handleFilterInput('dhcp_reservation')}
          />
          <IconButton onClick={handleClearFilter('dhcp_reservation')}>
            <CloseIcon />
          </IconButton>
        </MenuItem>
        <MenuItem onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            label={
              filterState['dhcp_state'] ? 'DHCP state:' + filterState['dhcp_state'] : 'DHCP state'
            }
            value={filterState['dhcp_state'] ? filterState['dhcp_state'] : ''}
            size="small"
            sx={{ ...filterStyles.textField }}
            onChange={handleFilterInput('dhcp_state')}
          />
          <IconButton onClick={handleClearFilter('dhcp_state')}>
            <CloseIcon />
          </IconButton>
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}
